import { useEffect } from 'react';

// Wires up the site's ambient motion: scroll progress + nav condense,
// scroll-reveal via IntersectionObserver, cursor glow, magnetic buttons,
// and 3D card tilt. All of it is inert under prefers-reduced-motion.
export default function useSiteMotion() {
  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const nav = document.querySelector('[data-nav]');
    const progressBar = document.querySelector('[data-progress]');
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      if (progressBar) {
        progressBar.style.width = `${max > 0 ? (h.scrollTop / max) * 100 : 0}%`;
      }
      if (nav) {
        const scrolled = h.scrollTop > 24;
        nav.classList.toggle('nav-scrolled', scrolled);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    let observer;
    const revealTargets = document.querySelectorAll('[data-reveal]');
    if (reduce) {
      revealTargets.forEach((el) => el.classList.add('in-view'));
    } else if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('in-view');
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
      );
      revealTargets.forEach((el) => observer.observe(el));
    } else {
      revealTargets.forEach((el) => el.classList.add('in-view'));
    }

    let onMouseMove;
    let onMouseOut;
    if (!reduce) {
      const glow = document.querySelector('[data-glow]');
      onMouseMove = (e) => {
        if (glow) {
          glow.style.opacity = '1';
          glow.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
        }
        const mag = e.target.closest && e.target.closest('[data-magnetic]');
        if (mag) {
          const r = mag.getBoundingClientRect();
          mag.style.transform = `translate(${(e.clientX - r.left - r.width / 2) * 0.28}px, ${(e.clientY - r.top - r.height / 2) * 0.4}px)`;
        }
        const tilt = e.target.closest && e.target.closest('[data-tilt]');
        if (tilt) {
          const r = tilt.getBoundingClientRect();
          const px = (e.clientX - r.left) / r.width - 0.5;
          const py = (e.clientY - r.top) / r.height - 0.5;
          tilt.style.transform = `perspective(900px) rotateY(${px * 7}deg) rotateX(${-py * 7}deg)`;
        }
      };
      onMouseOut = (e) => {
        const mag = e.target.closest && e.target.closest('[data-magnetic]');
        if (mag && (!e.relatedTarget || !mag.contains(e.relatedTarget))) mag.style.transform = 'translate(0, 0)';
        const tilt = e.target.closest && e.target.closest('[data-tilt]');
        if (tilt && (!e.relatedTarget || !tilt.contains(e.relatedTarget))) tilt.style.transform = 'perspective(900px) rotateY(0) rotateX(0)';
      };
      window.addEventListener('mousemove', onMouseMove, { passive: true });
      document.addEventListener('mouseout', onMouseOut);
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (observer) observer.disconnect();
      if (onMouseMove) window.removeEventListener('mousemove', onMouseMove);
      if (onMouseOut) document.removeEventListener('mouseout', onMouseOut);
    };
  }, []);
}
